import React, { useCallback } from 'react';
import { PopoverOrigin, TypographyProps } from '@mui/material';
import { OneOfPropertyElements } from '@shared/models/project.model';
import {
  ElementSelectPropertyCountType,
  IElementSelectProperty,
} from '@shared/models/element-property.model';
import {
  useSelectPropertyOptions,
  useSetElementSelectPropertyCount,
} from '@client/hooks/use-element-properties.hooks';
import { getCount } from '@shared/helpers/element-property.helpers';
import { PropertyMenu } from '@ui/menus/PropertyMenu';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';

interface ElementPropertySelectProps {
  property: IElementSelectProperty;
  element: OneOfPropertyElements;
  readonly?: boolean;
}

const ElementPropertySelect: React.FC<ElementPropertySelectProps> = ({
  property,
  element,
  readonly: readonlyParam,
}) => {
  const selectProperty = useSetElementSelectPropertyCount(element);
  const options = useSelectPropertyOptions(property);
  const count = getCount(property, false);
  const selectedOption = options.find((option) => option.value === count);
  const isReadonlyElement = useIsReadonlyElement(element);
  const readonly = readonlyParam || isReadonlyElement || property.readonly;

  const handleSelect = useCallback(
    (value: ElementSelectPropertyCountType) => {
      selectProperty(value, property);
    },
    [selectProperty, property],
  );

  return (
    <PropertyMenu
      disabled={readonly}
      options={options}
      selectedValue={count}
      selectedLabel={selectedOption?.label ?? count?.toString() ?? 'None'}
      isFallback={!property.count}
      buttonLabelProps={buttonLabelProps}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onSelect={handleSelect}
    />
  );
};

const buttonLabelProps: TypographyProps = {
  sx: {
    textTransform: 'capitalize',
  },
};
const anchorOrigin: PopoverOrigin = { vertical: 'bottom', horizontal: 'right' };
const transformOrigin: PopoverOrigin = { vertical: 'top', horizontal: 'right' };

export default ElementPropertySelect;
